import { useEffect, useRef, useState } from 'react'
import { MetricContainer } from '../MetricContainer/MetricContainer'
import { useFloating, inline, offset, flip, shift } from '@floating-ui/react-dom'
import * as d3 from 'd3'
import { Text } from 'blaze-ui/Text/Text'
import { countryCodeMap } from './countryCodeMap'
import Styles from './WorldMap.module.scss'
import { Spin } from 'common/Spin/Spin'

interface WorldMapProps {
  data?: {
    geographic_distribution: Array<{
      code: string
      percentage: number
    }>
  }
  isFetching: boolean
}

export const WorldMap = ({ data, isFetching }: WorldMapProps) => {
  const svgRef = useRef<SVGSVGElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })
  const [hoveredCountry, setHoveredCountry] = useState<{
    code: string
    percentage: number
    name: string
  } | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const { x, y, reference, floating, strategy } = useFloating({
    placement: 'top',
    middleware: [inline(), offset(8), flip(), shift({ padding: 4 })],
  })

  useEffect(() => {
    const updateDimensions = () => {
      if (wrapperRef.current) {
        const containerWidth = wrapperRef.current.getBoundingClientRect().width
        // Calculate height based on an aspect ratio, but constrain between min and max
        const calculatedHeight = containerWidth * 0.5
        const height = Math.min(Math.max(calculatedHeight, 300), 500)
        setDimensions({ width: containerWidth, height })
      }
    }

    window.addEventListener('resize', updateDimensions)
    updateDimensions()
    return () => window.removeEventListener('resize', updateDimensions)
  }, [])

  useEffect(() => {
    if (!data || !dimensions.width || !svgRef.current) return

    setIsLoading(true)
    setError(null)

    d3.select(svgRef.current).selectAll('*').remove()

    const svg = d3.select(svgRef.current).attr('width', dimensions.width).attr('height', dimensions.height)

    // Create a color scale using a two-part scaling approach
    const maxPercentage = Math.max(...data.geographic_distribution.map((d) => d.percentage))
    const minPercentage = Math.min(...data.geographic_distribution.map((d) => d.percentage))

    // Create two scales: one for lower values and one for higher values
    const breakpoint = 5

    const lowerScale = d3
      .scaleLog()
      .domain([Math.max(0.01, minPercentage), breakpoint])
      .range([0.1, 0.5])
      .clamp(true)

    const upperScale = d3.scalePow().exponent(0.5).domain([breakpoint, maxPercentage]).range([0.5, 0.95]).clamp(true)

    const getColorIntensity = (value: number) => {
      if (value <= 0) return `rgba(59, 130, 246, 0.1)`
      const intensity = value <= breakpoint ? lowerScale(value) : upperScale(value)
      return `rgba(59, 130, 246, ${intensity})`
    }
    // Build a quick lookup for country data using the countryCodeMap
    const countryDataMap = new Map(data.geographic_distribution.map((d) => [countryCodeMap[d.code] || d.code, d]))

    d3.json('https://raw.githubusercontent.com/holtzy/D3-graph-gallery/master/DATA/world.geojson')
      .then((worldData: any) => {
        if (!worldData) throw new Error('Failed to load map data')

        // Remove Antarctica
        const features = worldData.features.filter((f: any) => f.id !== 'ATA' && f.properties.name !== 'Antarctica')

        // Build a GeoJSON FeatureCollection
        const geojson = { type: 'FeatureCollection', features }

        // Use Natural Earth projection which is designed to show a continuous world.
        const projection = d3.geoNaturalEarth1().fitSize([dimensions.width, dimensions.height], geojson as any)

        const path = d3.geoPath(projection)

        svg
          .selectAll('path')
          .data(features)
          .enter()
          .append('path')
          .attr('d', path as any)
          .attr('fill', (d: any) => {
            const countryData = countryDataMap.get(d.id)

            return countryData && countryData.percentage > 0
              ? getColorIntensity(countryData.percentage)
              : 'var(--dark-3)'
          })
          .attr('stroke', 'rgba(59, 130, 246, 0.3)')
          .attr('stroke-width', 0.5)
          .on('mouseover', (event: any, d: any) => {
            const countryData = countryDataMap.get(d.id)
            setHoveredCountry({
              code: d.id,
              percentage: countryData ? countryData.percentage : 0,
              name: d.properties.name,
            })
            reference(event.target)
          })
          .on('mouseout', () => {
            setHoveredCountry(null)
            reference(null)
          })

        setIsLoading(false)
      })
      .catch((err) => {
        setError('Failed to load map data')
        setIsLoading(false)
      })
  }, [dimensions, data, reference])

  return (
    <MetricContainer name="Followers by Country" tooltip="Geographic distribution of followers">
      <div ref={wrapperRef} className={Styles.wrapper}>
        {(isLoading || isFetching) && (
          <div className={Styles.loadingOverlay} data-testid="loading">
            <Spin />
          </div>
        )}
        {error && (
          <div className={Styles.loadingOverley}>
            <div className={Styles.loadingText}>{error}</div>
          </div>
        )}
        <svg ref={svgRef} />
        {hoveredCountry && (
          <div
            ref={floating}
            className={Styles.tooltip}
            style={{
              position: strategy,
              top: y ?? 0,
              left: x ?? 0,
              zIndex: 1000,
            }}
          >
            <div className={Styles.tooltipSection}>
              <Text variant="smallList" color="var(--dark-80)">
                {hoveredCountry.name}
              </Text>
              <Text variant="metadata" color="var(--dark-60)">
                {hoveredCountry.percentage.toFixed(2)}% of followers
              </Text>
            </div>
          </div>
        )}
      </div>
    </MetricContainer>
  )
}
